import { useState } from 'react';
import {
  useAccountNotes, useSaveNote, useDeleteNote,
  useAccountContacts, useSaveContact, useDeleteContact,
  type AccountContact,
} from '../../lib/api/hooks-account.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Notes and contacts: the parts of an account record that are not telephony.
 *
 * Both are MSP-only. The endpoints behind them refuse a tenant-scoped caller
 * outright, so neither of these ever renders inside the customer's own portal --
 * a note reading "chasing payment, do not upsell" is written about the customer,
 * not for them.
 */

// ------------------------------------------------------------------- notes

export function AccountNotesTab({ tenantId }: { tenantId: string }) {
  const notes = useAccountNotes(tenantId);
  const save = useSaveNote(tenantId);
  const remove = useDeleteNote(tenantId);
  const can = useSession((s) => s.can);
  const [draft, setDraft] = useState('');

  const writable = can('tenant:write');

  if (notes.error) return <ErrorBanner error={notes.error} />;

  const rows = notes.data?.data ?? [];

  return (
    <>
      {writable && (
        <div className="arc-card" style={{ marginBottom: 12 }}>
          <div className="arc-card-body">
            <textarea
              className="arc-input"
              rows={3}
              placeholder="Add a note about this account…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={{ resize: 'vertical', width: '100%' }}
            />
            <div className="arc-row" style={{ justifyContent: 'flex-end', marginTop: 8, gap: 8 }}>
              <button
                type="button"
                className="arc-btn arc-btn-primary"
                disabled={!draft.trim() || save.isPending}
                onClick={() => {
                  save.mutate({ body: draft.trim() }, { onSuccess: () => setDraft('') });
                }}
              >
                <Icon name="plus" size={13} /> Add note
              </button>
            </div>
          </div>
        </div>
      )}

      {notes.isLoading ? (
        <div className="arc-card"><TableSkeleton rows={4} cols={1} /></div>
      ) : rows.length === 0 ? (
        <div className="arc-card">
          <Empty
            icon="edit"
            title="No notes on this account"
            hint="Anything the next person picking up this account should know — a pending move, a difficult contact, an agreed exception."
          />
        </div>
      ) : (
        <div className="arc-stack" style={{ gap: 8 }}>
          {rows.map((n) => (
            <div key={n.id} className="arc-card" data-pinned={n.is_pinned || undefined}>
              <div className="arc-card-body" style={{ paddingTop: 11, paddingBottom: 11 }}>
                <div className="arc-row" style={{ justifyContent: 'space-between', gap: 10 }}>
                  <div className="arc-row" style={{ gap: 7 }}>
                    {n.is_pinned && <Badge tone="warn">Pinned</Badge>}
                    <span style={{ fontWeight: 550 }}>{n.author_name ?? 'Unknown'}</span>
                    <span className="arc-subtle">{relativeTime(n.created_at)}</span>
                  </div>
                  {writable && (
                    <div className="arc-row" style={{ gap: 4 }}>
                      <button
                        type="button"
                        className="arc-btn arc-btn-ghost arc-btn-sm"
                        title={n.is_pinned ? 'Unpin' : 'Pin to the top'}
                        onClick={() => save.mutate({ id: n.id, is_pinned: !n.is_pinned })}
                      >
                        <Icon name={n.is_pinned ? 'close' : 'check'} size={12} />
                      </button>
                      <button
                        type="button"
                        className="arc-btn arc-btn-ghost arc-btn-sm"
                        title="Delete note"
                        onClick={() => remove.mutate(n.id)}
                      >
                        <Icon name="trash" size={12} />
                      </button>
                    </div>
                  )}
                </div>
                {/* Notes are written as prose with line breaks that carry meaning. */}
                <p style={{ marginTop: 7, whiteSpace: 'pre-wrap' }}>{n.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- contacts

const BLANK = {
  name: '', role: '', email: '', phone: '', notes: '', is_primary: false,
};

export function AccountContactsTab({ tenantId }: { tenantId: string }) {
  const contacts = useAccountContacts(tenantId);
  const save = useSaveContact(tenantId);
  const remove = useDeleteContact(tenantId);
  const can = useSession((s) => s.can);
  const [editing, setEditing] = useState<AccountContact | typeof BLANK | null>(null);

  const writable = can('tenant:write');

  if (contacts.error) return <ErrorBanner error={contacts.error} />;

  const rows = contacts.data?.data ?? [];

  return (
    <>
      <div className="arc-row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {writable && (
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => setEditing({ ...BLANK })}>
            <Icon name="plus" size={13} /> Add contact
          </button>
        )}
      </div>

      {editing && (
        <ContactEditor
          value={editing}
          pending={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(body) => {
            const id = 'id' in editing ? editing.id : undefined;
            save.mutate({ ...body, ...(id ? { id } : {}) }, {
              onSuccess: () => setEditing(null),
            });
          }}
        />
      )}

      <div className="arc-card">
        {contacts.isLoading ? (
          <TableSkeleton rows={4} cols={4} />
        ) : rows.length === 0 ? (
          <Empty
            icon="users"
            title="No contacts recorded"
            hint="The people to reach at this customer — billing, technical, whoever signs. Separate from portal logins, because most of them will not have one."
          />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Email</th><th>Phone</th>
                  {writable && <th style={{ width: 70 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <div className="arc-row" style={{ gap: 6 }}>
                        <span style={{ fontWeight: 550 }}>{k.name}</span>
                        {k.is_primary && <Badge tone="ok">Primary</Badge>}
                      </div>
                      {k.notes && (
                        <div className="arc-subtle" style={{ marginTop: 2 }}>{k.notes}</div>
                      )}
                    </td>
                    <td className="arc-subtle">{k.role ?? '—'}</td>
                    <td>
                      {k.email
                        ? <a href={`mailto:${k.email}`}>{k.email}</a>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-mono">{k.phone ?? '—'}</td>
                    {writable && (
                      <td>
                        <div className="arc-row" style={{ gap: 4 }}>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            title="Edit" onClick={() => setEditing(k)}>
                            <Icon name="edit" size={12} />
                          </button>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            title="Remove" onClick={() => remove.mutate(k.id)}>
                            <Icon name="trash" size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function ContactEditor({ value, pending, onCancel, onSave }: {
  value: AccountContact | typeof BLANK;
  pending: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    name: value.name ?? '',
    role: value.role ?? '',
    email: value.email ?? '',
    phone: value.phone ?? '',
    notes: value.notes ?? '',
    is_primary: value.is_primary ?? false,
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="arc-card" style={{ marginBottom: 12 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">{'id' in value ? 'Edit contact' : 'New contact'}</h2>
      </div>
      <div className="arc-card-body">
        <div className="arc-grid arc-grid-2" style={{ gap: 10 }}>
          <label className="arc-field">
            <span>Name</span>
            <input className="arc-input" value={form.name} onChange={set('name')} />
          </label>
          <label className="arc-field">
            <span>Role</span>
            <input className="arc-input" value={form.role} onChange={set('role')}
              placeholder="Billing, technical, signatory…" />
          </label>
          <label className="arc-field">
            <span>Email</span>
            <input className="arc-input" type="email" value={form.email} onChange={set('email')} />
          </label>
          <label className="arc-field">
            <span>Phone</span>
            <input className="arc-input" value={form.phone} onChange={set('phone')} />
          </label>
        </div>
        <label className="arc-field" style={{ marginTop: 10 }}>
          <span>Notes</span>
          <input className="arc-input" value={form.notes} onChange={set('notes')}
            placeholder="Best reached mornings; prefers email" />
        </label>
        <label className="arc-row" style={{ gap: 7, marginTop: 10 }}>
          <input type="checkbox" checked={form.is_primary}
            onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked }))} />
          <span>Primary contact for this account</span>
        </label>

        <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="arc-btn arc-btn-primary"
            disabled={!form.name.trim() || pending}
            onClick={() => onSave({
              name: form.name.trim(),
              role: form.role.trim() || null,
              /*
                Empty strings become null rather than being sent through. The
                API validates email as an email, and '' is not one -- sending it
                would fail the save with a validation error on a field the user
                deliberately left blank.
              */
              email: form.email.trim() || null,
              phone: form.phone.trim() || null,
              notes: form.notes.trim() || null,
              is_primary: form.is_primary,
            })}
          >
            Save contact
          </button>
        </div>
      </div>
    </div>
  );
}
